import { GRID_WIDTH, GRID_HEIGHT, COLORS } from '../constants/gameConstants';
import { ownFill } from './gameLogic';

const cloneGrid = (grid) => grid.map(row => [...row]);
const cloneOwnership = (ownership) => ownership.map(row => [...row]);

// Build a list of potential AI moves with their resulting ownership and gains
const getCandidateMoves = (
  grid,
  ownership,
  aiPosition,
  playerColor,
  gridWidth = GRID_WIDTH,
  gridHeight = GRID_HEIGHT
) => {
  const [x, y] = aiPosition;
  const currentColor = grid[x][y];
  const currentAiTiles = ownership.flat().filter(cell => cell === "AI").length;

  return COLORS.reduce((acc, color) => {
    // Skip player's current color; AI cannot pick it
    if (color === playerColor) {
      return acc;
    }

    const tempGrid = cloneGrid(grid);
    const tempOwnership = cloneOwnership(ownership);
    const { newOwnership, newGrid } = ownFill(
      tempGrid,
      tempOwnership,
      x,
      y,
      currentColor,
      color,
      "AI",
      gridWidth,
      gridHeight
    );

    const aiTiles = newOwnership.flat().filter(cell => cell === "AI").length;

    // Only keep moves that gain territory
    if (aiTiles > currentAiTiles) {
      acc.push({
        color,
        aiTiles,
        gain: aiTiles - currentAiTiles,
        newOwnership,
        newGrid
      });
    }

    return acc;
  }, []);
};

// Estimate player's best immediate gain after the AI takes a move
const estimatePlayerResponseGain = (
  grid,
  ownership,
  playerPosition,
  playerColor,
  aiColor,
  gridWidth = GRID_WIDTH,
  gridHeight = GRID_HEIGHT
) => {
  const [px, py] = playerPosition;
  const currentPlayerTiles = ownership.flat().filter(cell => cell === "Player").length;

  let maxGain = 0;

  COLORS.forEach(color => {
    // Player cannot pick AI's color
    if (color === aiColor) {
      return;
    }

    const tempGrid = cloneGrid(grid);
    const tempOwnership = cloneOwnership(ownership);
    const { newOwnership } = ownFill(
      tempGrid,
      tempOwnership,
      px,
      py,
      playerColor,
      color,
      "Player",
      gridWidth,
      gridHeight
    );

    const playerTiles = newOwnership.flat().filter(cell => cell === "Player").length;
    const gain = playerTiles - currentPlayerTiles;
    if (gain > maxGain) {
      maxGain = gain;
    }
  });

  return maxGain;
};

// Pick a move based on the chosen difficulty level
export const getAiMoveByDifficulty = (
  grid,
  ownership,
  aiPosition,
  playerPosition,
  playerColor,
  aiColor,
  difficulty = "normal",
  gridWidth = GRID_WIDTH,
  gridHeight = GRID_HEIGHT
) => {
  const candidates = getCandidateMoves(
    grid,
    ownership,
    aiPosition,
    playerColor,
    gridWidth,
    gridHeight
  );

  if (candidates.length === 0) {
    // Fallback: pick any non-player color to avoid returning null
    return COLORS.find(c => c !== playerColor && c !== aiColor) || COLORS[0];
  }

  if (difficulty === "easy") {
    // Random valid move
    const randomIndex = Math.floor(Math.random() * candidates.length);
    return candidates[randomIndex].color;
  }

  if (difficulty === "normal") {
    // Current behavior: maximize immediate gain
    return candidates.reduce((best, move) => {
      if (!best || move.aiTiles > best.aiTiles) {
        return move;
      }
      return best;
    }, null).color;
  }

  // Hard: balance own gain vs. opponent's best immediate response
  let bestMove = candidates[0];
  let bestScore = -Infinity;

  candidates.forEach(move => {
    const opponentPotentialGain = estimatePlayerResponseGain(
      move.newGrid,
      move.newOwnership,
      playerPosition,
      playerColor,
      move.color,
      gridWidth,
      gridHeight
    );

    // Weight immediate gain higher than opponent's response
    const score = move.gain * 1.0 - opponentPotentialGain * 0.6;
    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  });

  return bestMove.color;
};